import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';
import { FilterUpdateEvent, ThemeEvents, DialogCloseEvent, DialogOpenEvent } from '@theme/events';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/events';
import { morph } from '@theme/morph';


/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';


/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  #columnSettings = ['--columns', '--columns-laptop', '--columns-tablet', '--columns-mobile'];

  /**
   * Serialized filter params of the last committed state. In deferred-apply
   * mode, edits only stage a draft; this snapshot is what Apply commits and
   * what an un-applied dialog close restores.
   * @type {string | null}
   */
  #committedParams = null;

  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();
    this.#snapshotCommitted();

    // Refs are not guaranteed to be collected yet, so the apply-mode check
    // happens inside the handlers rather than gating registration here.
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#snapshotCommitted);
    this.#dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.#snapshotCommitted);
    this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.#dialogComponent = null;
  }

  /**
   * Deferred-apply mode: form edits stage a draft with a live preview count
   * instead of re-rendering results; the Apply button commits (see applyDraft).
   */
  get #applyMode() {
    return this.refs.facetsForm?.dataset.applyMode === 'submit';
  }

  #snapshotCommitted = () => {
    if (!this.refs.facetsForm) return;
    this.#committedParams = this.createURLParameters().toString();
  };

  /**
   * After any SRA re-render the server-rendered form matches the committed URL.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#snapshotCommitted();
  }

  #handleDialogClose = () => {
    if (!this.#applyMode) return;
    if (this.createURLParameters().toString() === this.#committedParams) return;
    this.#restoreCommittedInputs();
  };

  /**
   * Creates URL parameters from form data
   * Cleans up empty price filters and preserves search query
   * @param {FormData} [formData] - Optional form data, defaults to current form
   * @returns {URLSearchParams} Processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    // Remove empty price filters (they cause issues)
    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Always reset to first page when filtering
    newParameters.delete('page');

    // Preserve search query if present
    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets search query parameter from current URL.
   * @returns {string} Search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates browser URL with current filter parameters
   * Uses pushState to update URL without page reload
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    // Clear existing params and add new ones
    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section. In deferred-apply mode this only
   * stages the draft (live preview count); Apply commits via applyDraft().
   */
  updateFilters = async () => {
    if (this.#applyMode) {
      this.stageFilters();
      return;
    }
    await this.#commitFilters();
  };

  /**
   * Applies the staged draft in deferred-apply mode: commits pending changes
   * and closes the containing dialog, if any.
   */
  applyDraft = async () => {
    const staged = this.createURLParameters().toString();
    if (staged !== this.#committedParams) {
      await this.#commitFilters();
      this.#committedParams = staged;
    }

    this.closest('dialog-component')?.closeDialog();
  };

  /**
   * Stages the current draft without committing: prefetches the filtered
   * section (warmed cache makes the later Apply instant) and updates the
   * result count preview on the Apply button.
   */
  stageFilters = debounce(async () => {
    if (!this.#applyMode) return;

    const urlParameters = this.createURLParameters();
    const url = new URL(window.location.pathname, window.location.origin);
    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const stagedCount =
        Number(
          new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector('facets-form-component')
            ?.dataset.resultsCount
        ) || 0;
      this.#updateApplyCount(stagedCount);
    } catch {
      // Cache/network failures keep the previous preview count.
    }
  }, 200);

  /**
   * Updates the staged count shown inside the paired Apply button.
   * @param {number} count - Staged results count
   */
  #updateApplyCount(count) {
    const applyComponent = this.parentElement?.querySelector('facet-apply-component');
    applyComponent?.updateCount(count);
  }

  /**
   * Restores form inputs to the last committed state (discards the draft).
   */
  #restoreCommittedInputs() {
    const committed = new URLSearchParams(this.#committedParams ?? '');
    const form = this.refs.facetsForm;

    form?.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = committed.has(input.name, input.value);
      }
    });

    form?.querySelectorAll('price-facet-component').forEach((component) => {
      this.#restorePriceFacet(component, committed);
    });

    this.stageFilters();
  }

  /**
   * Restores a price facet's inputs, range sliders and track visuals.
   * @param {PriceFacetComponent} component - The price facet component
   * @param {URLSearchParams} committed - Committed filter params
   */
  #restorePriceFacet(component, committed) {
    const { minPriceInput, maxPriceInput, minPriceRange, maxPriceRange } = component;
    if (!minPriceInput || !maxPriceInput || !minPriceRange || !maxPriceRange) return;

    minPriceInput.value = committed.get(minPriceInput.name) ?? '';
    maxPriceInput.value = committed.get(maxPriceInput.name) ?? '';

    const rangeMax = Number(minPriceRange.max) || 0;
    const minValue = Number(minPriceInput.value) || 0;
    const maxValue = maxPriceInput.value === '' ? rangeMax : Number(maxPriceInput.value) || 0;
    const clampedMin = Math.min(minValue, maxValue);
    const clampedMax = Math.max(minValue, maxValue);

    minPriceRange.value = String(clampedMin);
    maxPriceRange.value = String(clampedMax);

    const wrapper = minPriceRange.parentElement;
    if (wrapper instanceof HTMLElement) {
      wrapper.style.setProperty('--price-range-min', `${(clampedMin / rangeMax) * 100}%`);
      wrapper.style.setProperty('--price-range-max', `${(clampedMax / rangeMax) * 100}%`);
    }

    minPriceRange.classList.toggle('price-facet__range--on-top', rangeMax > 0 && clampedMin > rangeMax / 2);
  }

  /**
   * Commits the current form state: pushes the URL and re-renders results.
   */
  #commitFilters = async () => {
    this.#updateURLHash();

    // Cache is disabled in design mode, so those renders behave like network.
    const cached = !Shopify.designMode && sectionRenderer.isCached(this.sectionId);

    if (cached) {
      const renderPromise = this.#updateSection();
      this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
      this.#dispatchStandardFilterEvent(renderPromise);
      await renderPromise;
    } else {
      this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
      const renderPromise = this.#updateSection();
      this.#dispatchStandardFilterEvent(renderPromise);
      await renderPromise;
    }
  };

  /**
   * Emits the Shopify standard collection:update / search:update event after a
   * filter/sort change, resolving its promise once the section has re-rendered.
   * @param {Promise<void>} renderPromise
   */
  #dispatchStandardFilterEvent(renderPromise) {
    const query = this.#getSearchQuery();

    if (query) {
      const deferred = SearchUpdateEvent.createPromise();
      const productFilters = SearchUpdateEvent.parseProductFilters();
      this.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query,
            ...(productFilters && { productFilters }),
            sortKey: SearchUpdateEvent.getSortKey(),
          },
          promise: deferred.promise,
        })
      );
      renderPromise
        .then(() => deferred.resolve({ totalCount: this.#getResultsCount() }))
        .catch((error) => deferred.reject(error));
    } else {
      const deferred = CollectionUpdateEvent.createPromise();
      const productFilters = CollectionUpdateEvent.parseProductFilters();
      const rawId = this.dataset.collectionId;
      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: {
            id: rawId && /^\d+$/.test(rawId) ? rawId : null,
            handle: this.dataset.collectionHandle,
            productsCount: this.#getResultsCount(),
          },
          ...(productFilters && { productFilters }),
          sortKey: CollectionUpdateEvent.getSortKey(),
          promise: deferred.promise,
        })
      );
      renderPromise
        .then(() => deferred.resolve({ productsCount: this.#getResultsCount() }))
        .catch((error) => deferred.reject(error));
    }
  }

  /**
   * @returns {number} The current product/result count from the rendered markup.
   */
  #getResultsCount() {
    return Number(this.dataset.resultsCount) || 0;
  }

  /**
   * Updates the section.
   */
  #updateSection = async () => {
    const dialog = this.closest('.dialog');
    const viewTransition = !dialog || dialog?.hasAttribute('start-view-transition');
    await this.#renderMainCollectionSection(viewTransition);
  }

  /**
   * Renders the main collection section with filters applied.
   * @param {boolean} [viewTransition] - Whether to use view transition
   */
  async #renderMainCollectionSection(viewTransition) {
    const sectionId = this.sectionId;

    const cache = !Shopify.designMode;

    sectionRenderer.abortPendingMorph(sectionId);

    const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
    if (!sectionElement) return;

    const isCached = cache && sectionRenderer.isCached(sectionId);
    if (!isCached) sectionElement.classList.add('loading-state');

    const abortController = new AbortController();
    sectionRenderer.abortControllersBySectionId.set(sectionId, abortController);
    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, cache);

      if (!abortController.signal.aborted) {
        const updatedSectionHTML = this.#updateColumnSettingsInHTML(sectionId, sectionHTML);

        await morphSection(sectionId, updatedSectionHTML);
        sectionElement.classList.remove('loading-state');

        await this.#renderProductGrid(sectionId, updatedSectionHTML, viewTransition);
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.warn('Failed to render collection section', { sectionId, error });
      }
    } finally {
      sectionRenderer.abortControllersBySectionId.delete(sectionId);
      sectionElement.classList.remove('loading-state');
    }
  }

  /**
   * Updates column settings in the new section HTML before morphing.
   * @param {string} sectionId - The section ID
   * @param {string} sectionHTML - The new section HTML
   * @returns {string} The updated section HTML with preserved column settings
   */
  #updateColumnSettingsInHTML(sectionId, sectionHTML) {
    const existingSection = document.getElementById(`shopify-section-${sectionId}`);
    if (!existingSection) return sectionHTML;

    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newSection = fragment.getElementById(`shopify-section-${sectionId}`);
    if (!newSection) return sectionHTML;

    const existingGrid = existingSection.querySelector('.product-grid');
    const newGrid = newSection.querySelector('.product-grid');

    if (!existingGrid || !newGrid) return sectionHTML;

    this.#columnSettings.forEach(prop => {
      const value = existingGrid.style.getPropertyValue(prop);
      if (value) {
        newGrid.style.setProperty(prop, value);
      }
    });

    // Return updated HTML - morphSection expects full HTML document
    return fragment.documentElement.outerHTML;
  }

  /**
   * Renders the product grid with preserved column settings.
   * @param {string} sectionId - The section ID
   * @param {string} sectionHTML - The section HTML
   * @param {boolean} [viewTransition] - Whether to use view transition
   */
  async #renderProductGrid(sectionId, sectionHTML, viewTransition) {
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const existingSection = document.getElementById(`shopify-section-${sectionId}`);
    const newSection = fragment.getElementById(`shopify-section-${sectionId}`);
    if (!existingSection || !newSection) return;

    const existingProductGrid = existingSection.querySelector('.product-grid-wrapper');
    const newProductGrid = newSection.querySelector('.product-grid-wrapper');

    if (!existingProductGrid || !newProductGrid) return;

    existingProductGrid.removeAttribute('data-skip-subtree-update');
    newProductGrid.removeAttribute('data-skip-subtree-update');

    // Preserve column settings in design mode
    if (Shopify.designMode) {
      this.#preserveColumnSettings(existingProductGrid, newProductGrid);
    }

    if (viewTransition) {
      await this.#waitForScrollEnd();
      await startViewTransition(() => morph(existingProductGrid, newProductGrid), ['product-grid']);
    } else {
      morph(existingProductGrid, newProductGrid);
      // Wait for DOM to update after morph
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }
  }

  /**
   * Preserves column settings from existing grid to new grid.
   * Only used in Shopify design mode to maintain user's column selections.
   * @param {HTMLElement} existingProductGrid - The existing product grid wrapper
   * @param {HTMLElement} newProductGrid - The new product grid wrapper
   */
  #preserveColumnSettings(existingProductGrid, newProductGrid) {
    const existingGrid = existingProductGrid.querySelector('.product-grid');
    const newGrid = newProductGrid.querySelector('.product-grid');

    if (!existingGrid || !newGrid) return;

    ['--columns', '--columns-laptop', '--columns-tablet', '--columns-mobile'].forEach(prop => {
      const value = existingGrid.style.getPropertyValue(prop);
      if (value) newGrid.style.setProperty(prop, value);
    });
  }

  /**
   * Updates filters based on provided URL.
   * @param {string} url - URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    const renderPromise = this.#updateSection();
    this.#dispatchStandardFilterEvent(renderPromise);
  }

  /**
   * Resolves 100ms after the last scroll event, or immediately if scroll is
   * already settled. Prevents ViewTransition from capturing snapshots while
   * a smooth scroll is still in progress.
   * @param {number} [delay=100]
   * @returns {Promise<void>}
   */
  #waitForScrollEnd(delay = 100) {
    return new Promise((resolve) => {
      let timer;
      const onScroll = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          window.removeEventListener('scroll', onScroll);
          resolve();
        }, delay);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    });
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and selected facet summary.
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keydown events for facets form.
   * @param {KeyboardEvent} event - Keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels.
   * @param {MouseEvent} event - Mouseover event
   */
  /**
   * Prefetches filtered results on hover for faster response
   * Debounced to avoid excessive requests
   * @param {Event} event - Mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    // Build form data with hovered option included
    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    // If option is already checked, remove it from URL (toggle behavior)
    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  /**
   * Cancels prefetch page operation.
   */
  cancelPrefetchPage = () => this.prefetchPage.cancel();

}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#handleSortingOnMobile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Handles keyboard navigation in sorting dropdown.
   * @param {KeyboardEvent} event - Keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event.
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options.
   * @param {Element[]} options - Option elements
   * @param {number} newIndex - Index of option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects option and triggers form submission.
   * @param {Element} option - Option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes dropdown and returns focus to summary.
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting.
   * @param {Event} event - Change event
   */
  updateFilterAndSorting(event) {
    event.stopPropagation();
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const details = this.querySelector('details');
    if (details) {
      if (details.hasAttribute('data-close-details-on-select')) {
        details.open = false;
      }
    }

    facetsForm.updateFilters();
  }

  /**
   * Triggers filter and sorting from drawer.
   * @param {Event} event - Click event
   */
  triggerFilterAndSorting(event) {
    event.stopPropagation();
    const resultsList = this.closest('results-list');

    if (!resultsList) return;

    const filtersDrawer = resultsList.querySelector('#filters-drawer');

    if (!filtersDrawer) return;
    const dialog = filtersDrawer.querySelector('.dialog');
    const sortingDrawer = filtersDrawer.querySelector('sorting-filter-component');
    if (!sortingDrawer) return;

    const sortValue = this.querySelector('input[name="sort_by"]:checked')?.value;
    if (!sortValue) return;

    sortingDrawer.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      if (input.value === sortValue) {
        if (dialog) {
          dialog.setAttribute('start-view-transition', '');
        }
        input.click();
        if (dialog) {
          dialog.removeAttribute('start-view-transition');
        }
      }
    });

    const { details } = this.refs;
    if (!details) return;
    details.open = false;
  }

  /**
   * Handles sorting filter visibility on mobile devices.
   */
  #handleSortingOnMobile() {
    const resultsListContainer = this.closest('results-list');

    if (!resultsListContainer) return;

    const mobileSortingDrawer = resultsListContainer.querySelector('.sorting-filter--listbox');
    const desktopSortingToolbar = resultsListContainer.querySelector('.sorting-filter--dropdown');

    if (!mobileSortingDrawer) return;

    if (!desktopSortingToolbar) {
      mobileSortingDrawer.hidden = true;
    }
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.boundMinPriceInputSelect = () => this.minPriceInput.select();
    this.boundMaxPriceInputSelect = () => this.maxPriceInput.select();
    this.minPriceInput.addEventListener('focus', this.boundMinPriceInputSelect);
    this.maxPriceInput.addEventListener('focus', this.boundMaxPriceInputSelect);

    this.boundHandleMinPriceInputChange = this.handleMinPriceInputChange.bind(this);
    this.boundHandleMaxPriceInputChange = this.handleMaxPriceInputChange.bind(this);
    this.minPriceInput.addEventListener('change', this.boundHandleMinPriceInputChange);
    this.maxPriceInput.addEventListener('change', this.boundHandleMaxPriceInputChange);

    this.boundHandleMinRangeSliderChange = this.handleMinRangeSliderChange.bind(this);
    this.boundHandleMaxRangeSliderChange = this.handleMaxRangeSliderChange.bind(this);
    this.minPriceRange.addEventListener('change', this.boundHandleMinRangeSliderChange);
    this.maxPriceRange.addEventListener('change', this.boundHandleMaxRangeSliderChange);

    this.boundHandleMinRangeSliderInput = this.handleMinRangeSliderInput.bind(this);
    this.boundHandleMaxRangeSliderInput = this.handleMaxRangeSliderInput.bind(this);
    this.minPriceRange.addEventListener('input', this.boundHandleMinRangeSliderInput);
    this.maxPriceRange.addEventListener('input', this.boundHandleMaxRangeSliderInput);

    this.addEventListener('keydown', this.#onKeyDown);

    this.#updateThumbLayering();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#updateThumbLayering();
  }

  #updateThumbLayering() {
    const value = parseInt(this.minPriceRange.value);
    const max = parseInt(this.minPriceRange.max);
    const raiseMin = Number.isFinite(value) && Number.isFinite(max) && max > 0 && value > max / 2;
    this.minPriceRange.classList.toggle('price-facet__range--on-top', raiseMin);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.boundMinPriceInputSelect) {
      this.minPriceInput.removeEventListener('focus', this.boundMinPriceInputSelect);
    }
    if (this.boundMaxPriceInputSelect) {
      this.maxPriceInput.removeEventListener('focus', this.boundMaxPriceInputSelect);
    }

    if (this.boundHandleMinPriceInputChange) {
      this.minPriceInput.removeEventListener('change', this.boundHandleMinPriceInputChange);
    }
    if (this.boundHandleMaxPriceInputChange) {
      this.maxPriceInput.removeEventListener('change', this.boundHandleMaxPriceInputChange);
    }

    if (this.boundHandleMinRangeSliderChange) {
      this.minPriceRange.removeEventListener('change', this.boundHandleMinRangeSliderChange);
    }
    if (this.boundHandleMaxRangeSliderChange) {
      this.maxPriceRange.removeEventListener('change', this.boundHandleMaxRangeSliderChange);
    }

    if (this.boundHandleMinRangeSliderInput) {
      this.minPriceRange.removeEventListener('input', this.boundHandleMinRangeSliderInput);
    }
    if (this.boundHandleMaxRangeSliderInput) {
      this.maxPriceRange.removeEventListener('input', this.boundHandleMaxRangeSliderInput);
    }

    this.removeEventListener('keydown', this.#onKeyDown);
  }


  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Handles min price input change.
   * @param {Event} event - Input change event
   */
  /**
   * Handles min price input change
   * Syncs text input with range slider and updates visual position
   * Ensures min doesn't exceed max value
   */
  handleMinPriceInputChange(event) {
    event.preventDefault();
    const currentValue = parseInt(event.target.value);
    const maxValue = parseInt(this.maxPriceInput.value || event.target.max);

    // Clamp min value to not exceed max
    const visualValue = Math.min(currentValue, maxValue);

    // Update CSS custom property for visual slider position
    this.minPriceRange.parentElement.style.setProperty(
      '--price-range-min',
      `${(visualValue / parseInt(this.minPriceRange.max)) * 100}%`
    );

    // Sync range slider value with input
    this.minPriceRange.value = visualValue;
    this.#updateThumbLayering();
  }

  /**
   * Handles max price input change.
   * @param {Event} event - Input change event
   */
  /**
   * Handles max price input change
   * Syncs text input with range slider and updates visual position
   * Ensures max doesn't go below min value
   */
  handleMaxPriceInputChange(event) {
    event.preventDefault();
    const currentValue = parseInt(event.target.value);
    const minValue = parseInt(this.minPriceInput.value || event.target.min);

    // Clamp max value to not go below min
    const visualValue = Math.max(currentValue, minValue);

    // Update CSS custom property for visual slider position
    this.maxPriceRange.parentElement.style.setProperty(
      '--price-range-max',
      `${(visualValue / parseInt(this.maxPriceRange.max)) * 100}%`
    );

    // Sync range slider value with input
    this.maxPriceRange.value = visualValue;
  }

  /**
   * Handles min range slider change.
   * @param {Event} event - Slider change event
   */
  handleMinRangeSliderChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Handles max range slider change.
   * @param {Event} event - Slider change event
   */
  handleMaxRangeSliderChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Handles min range slider input.
   * @param {Event} event - Slider input event
   */
  handleMinRangeSliderInput(event) {
    const maxValue = parseInt(this.maxPriceInput.value || event.target.max);
    event.target.value = Math.min(
      parseInt(event.target.value),
      maxValue
    );
    event.target.parentElement.style.setProperty(
      '--price-range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
    this.#updateThumbLayering();
  }

  /**
   * Handles max range slider input.
   * @param {Event} event - Slider input event
   */
  handleMaxRangeSliderInput(event) {
    const minValue = parseInt(this.minPriceInput.value || event.target.min);
    event.target.value = Math.max(
      parseInt(event.target.value),
      minValue
    );
    event.target.parentElement.style.setProperty(
      '--price-range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }

  /**
   * Updates price filter and results.
   */
  updatePriceFilterAndResults() {
    this.#adjustToValidValues(this.minPriceInput);
    this.#adjustToValidValues(this.maxPriceInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
  }

  /**
   * Adjusts input values to be within valid range.
   * @param {HTMLInputElement} input - Input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for inputs.
   */
  #setMinAndMaxValues() {
    if (this.maxPriceInput.value) this.minPriceInput.setAttribute('max', this.maxPriceInput.value);
    if (this.minPriceInput.value) this.maxPriceInput.setAttribute('min', this.minPriceInput.value);
    if (this.minPriceInput.value === '') this.maxPriceInput.setAttribute('min', '0');
    if (this.maxPriceInput.value === '') this.minPriceInput.setAttribute('max', this.maxPriceInput.getAttribute('max') ?? '');
  }


  get minPriceRange() {
    return this.querySelector('.price-facet__range-min');
  }

  get maxPriceRange() {
    return this.querySelector('.price-facet__range-max');
  }

  get minPriceInput() {
    return this.querySelector('.price-facet__input-min');
  }

  get maxPriceInput() {
    return this.querySelector('.price-facet__input-max');
  }
}
if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter.
   * @param {Event} event - Click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events.
   * @param {KeyboardEvent} event - Keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggles clear button visibility when filters are applied.
   * @param {FilterUpdateEvent} event - Filter update event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter.
   * @param {Object} data - Data object
   * @param {string} data.form - Form to remove filter from
   * @param {Event} event - Click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    event.stopPropagation();

    const url = this.dataset.url;
    if (!url) return;

    // The id names the form element, so climb to the component that wraps it
    const target = form ? document.getElementById(form) : this;
    const facetsForm = target?.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggles clear button visibility when filters are applied.
   * @param {FilterUpdateEvent} event - Filter update event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Applies the staged facet draft of its paired facets form (deferred-apply
 * mode) and closes the containing dialog.
 * @extends {Component}
 */
class FacetApplyComponent extends Component {
  /**
   * Applies staged changes; closes the containing dialog when present.
   * @param {Object} data - Data object
   * @param {string} data.form - Form id of the paired facets form
   * @param {Event} event - Click event
   */
  applyFilters({ form }, event) {
    event?.stopPropagation();

    const target = form ? document.getElementById(form) : this;
    const facetsForm = target?.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.applyDraft();
  }

  /**
   * Updates the staged result count shown inside the Apply button.
   * @param {number} count - Staged results count
   */
  updateCount(count) {
    const button = this.querySelector('button');
    const countLabel = this.querySelector('[data-apply-count]');

    if (button instanceof HTMLButtonElement) button.disabled = count === 0;
    if (countLabel instanceof HTMLElement) countLabel.textContent = `(${count})`;
  }
}

if (!customElements.get('facet-apply-component')) {
  customElements.define('facet-apply-component', FacetApplyComponent);
}